import React from 'react';
import Box from '@nubank/nuds-web/components/Box/Box';
import styled, { css } from 'styled-components';
import Image from '@nubank/nuds-web/components/Image/Image';
import Typography from '@nubank/nuds-web/components/Typography/Typography';
import Link from '@nubank/nuds-web/components/Link/Link';
import Grid from '@nubank/nuds-web/components/Grid/Grid';
import { breakpointsMedia, useBreakpointsMediaUp } from '@nubank/nuds-web/styles/breakpoints';
import { useParallax } from 'react-scroll-parallax';

import { useDynamicYieldValues } from '../../../../utils/dynamicYield';

const StyledRowContainer = styled.div`
  align-items: center;
  background-color: #f8f8fa;
  border-radius: 12px;
  display: inline-flex;
  gap: 20px;
  justify-content: left;
  position: relative;
  width: 100%;
  margin-top: 15px;
  ${breakpointsMedia({
    xs: css`
      padding: 30px;
    `,
    md: css`
      padding: 30px 80px;
    `,
    lg: css`
      padding: 30px;
    `,
  })}
`;

const StyledIconAndTitleContainer = styled.div`
  position: relative;
  background-color: #f8f8fa;
  border-radius: 12px;
  padding-left: 31px;
  padding-right: 31px;
  padding-bottom: 40px;

  h3 {
    ${breakpointsMedia({
    xs: css`
      font-size: 27px;
    `,
    md: css`
      font-size: 28px;
    `,
    lg: css`
      font-size: 26px;
    `,
    xl: css`
      font-size: 30px;
    `,
  })}
  }

  span {
    /* stylelint-disable property-no-vendor-prefix, property-no-unknown */
    background-image: linear-gradient(99.92deg, #A376FF 18.57%, #7B4DD6 71.77%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }
`;

const StyledCardBackgroundImagesContainer = styled.div`
  align-items: center;
  display: flex;
  gap: 17px;
  justify-content: center;
  max-width: 1391px;
  margin: 0 auto;
`;

const StyledTitleIcon = styled(Image)`
  position: absolute;
  margin-top: -50px;

  ${breakpointsMedia({
    md: css`
      left: 12%;
    `,
    lg: css`
      left: 17%;
      margin-top: -80px;
    `,
  })}

`;

const StyledListContainer = styled(Box)`
  ${breakpointsMedia({
    lg: css`
      gap: 20px;
    `,
  })}
`;

const StyledLink = styled(Link)`
  color: #7B4DD6;
`;

const StyledTypography = styled(Typography)`
  span {
    font-weight: bold;
  }
`;

function CuentaMGM() {
  const { values: dynamicYieldValues } = useDynamicYieldValues();
  const { ref } = useParallax({ speed: 20, easing: [0, 1, 1, -0.3], disabled: useBreakpointsMediaUp('lg') });
  return (
    <Box
      tag="section"
      height="100%"
      width="100%"
      marginBottom={{ xs: '20x', lg: '100px' }}
      paddingHorizontal={{ xs: '0', lg: '56px', xl: '20x' }}

    >
      <Grid
        paddingLeft={{ xs: '0', lg: '0x' }}
        paddingRight={{ xs: '0', lg: '0' }}
      >

        {/* CONTENT BOX */}

        <Grid.Child
          gridColumn={{ xs: '1 / span 4', md: '2 / span 4', lg: '2 / span 10' }}
          gridRow="1"
        >
          {/* CARD BACKGROUND IMAGES */}

          <StyledCardBackgroundImagesContainer
            ref={ref}
          >
            <Image
              alt="Nubank"
              srcSet={{
                xs: 'mgm/cuenta/card-background-left-xs.jpg',
                lg: 'mgm/cuenta/card-background-left-lg.jpg',
              }}
              loading="lazy"
            />
            <Image
              alt="Nubank"
              srcSet={{
                xs: 'mgm/cuenta/card-background-right-xs.jpg',
                lg: 'mgm/cuenta/card-background-right-lg.jpg',
              }}
              loading="lazy"
            />
          </StyledCardBackgroundImagesContainer>

          {/* CONTENT ROWS */}

          <Box
            position="relative"
            marginTop={{ xs: '-70px', md: '-20px', lg: '-50px' }}
            maxWidth={{ xs: '100%', lg: '1215px' }}
            paddingHorizontal={{ xs: '32px', lg: '12x' }}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            margin="0 auto"
          >
            <StyledIconAndTitleContainer>
              <StyledTitleIcon
                webp={false}
                alt="Nu"
                src="mgm/cuenta/card-new.svg"
                width={{ xs: '100px', md: '80px', lg: '100px' }}
                loading="lazy"
              />
              <Typography
                strong
                variant="heading3"
                height="100%"
                paddingTop={{ xs: '70px', md: '50px', lg: '40px' }}
                paddingHorizontal={{ xs: '0', md: '12x', lg: '8x' }}
                whiteSpace={{ xs: 'pre-line', lg: 'initial' }}
                textAlign={{ xs: 'left', lg: 'center' }}
              >
                En
                {' '}
                <span>Cuenta Nu de Débito </span>
                tu dinero puede crecer hasta un
                <span>
                  {' '}
                  {dynamicYieldValues.dynamicYieldTurbo}
                  % anual
                  {' '}
                </span>
              </Typography>
            </StyledIconAndTitleContainer>

            <StyledListContainer
              display="flex"
              flexDirection={{ xs: 'column', lg: 'row' }}
              marginTop={{ xs: '0', lg: '24px' }}
            >
              <StyledRowContainer>
                <Image
                  alt="Sin anualidad"
                  src="mgm/cuenta/icon-1.svg"
                  webp={false}
                  width="49px"
                  maxWidth="49px"
                  loading="lazy"
                />
                <span>
                  <StyledTypography variant="paragraph2">
                    Crea una
                    {' '}
                    <span>Cajita Nu </span>
                    {' '}
                    para que tu dinero crezca
                    {' '}
                    <span>
                      {dynamicYieldValues.dynamicYield}
                      % al año.
                    </span>
                    {' '}
                    También puedes crear una
                    {' '}
                    <span>
                      Cajita Turbo
                    </span>
                    {' '}
                    para que crezca hasta un
                    {' '}
                    <span>
                      {dynamicYieldValues.dynamicYieldTurbo}
                      % anual
                    </span>
                  </StyledTypography>
                </span>
              </StyledRowContainer>

              <StyledRowContainer>
                <Image
                  alt="Sin penalizaciones por uso mínimo"
                  src="mgm/cuenta/icon-2.svg"
                  webp={false}
                  width="49px"
                  maxWidth="49px"
                  loading="lazy"
                />
                <span>
                  <Typography strong variant="paragraph2" tag="span">
                    Compra en tiendas o en línea y retira efectivo
                  </Typography>
                  <Typography variant="paragraph2" tag="span">
                    {' '}
                    con la tarjeta de débito Mastercard.
                  </Typography>
                </span>
              </StyledRowContainer>

              <StyledRowContainer>
                <Image
                  alt="Sin cobros por reposición si pierdes o roban tu Nu"
                  src="mgm/cuenta/icon-3.svg"
                  webp={false}
                  width="49px"
                  maxWidth="49px"
                  loading="lazy"
                />
                <span>
                  <Typography strong variant="paragraph2" tag="span">
                    Controla tu dinero desde la app.
                  </Typography>
                  <Typography variant="paragraph2" tag="span">
                    {' '}
                    Regrésalo de tu Cajita a Cuenta Nu para usarlo cuando quieras.
                  </Typography>
                </span>
              </StyledRowContainer>
            </StyledListContainer>

          </Box>

          {/* LINK */}

          <Box
            position="relative"
            maxWidth={{ xs: '100%' }}
            paddingHorizontal={{ xs: '32px', lg: '0' }}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            marginTop={{ xs: '4x', lg: '8x' }}
          >
            <StyledLink
              intlKey="MGM.CUENTA.LINK"
              href="/cuenta"
              variant="action"
              color="primary"
              alignSelf="center"
            />
          </Box>
        </Grid.Child>

      </Grid>
    </Box>
  );
}

export default CuentaMGM;
